import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Clock } from "lucide-react";
import CTABanner from "../components/CTABanner";
import NewsletterSignup from "../components/NewsletterSignup";
import StarMotif from "../components/StarMotif";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const PLACEHOLDER_TITLES = [
  "Five questions every organisation should ask before adopting AI",
  "Why PMOs need to focus on decisions, not administration",
  "Supplier renewal risk: the hidden cost of poor contract visibility",
  "Why transformation benefits are often promised but not realised",
  "Communication is not adoption: the case for structured change impact assessment",
];

const Insights = () => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/insights`)
      .then((res) => {
        if (alive) setInsights(res.data || []);
      })
      .catch(() => {
        if (alive) setInsights([]);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const showPlaceholders = !loading && insights.length === 0;

  return (
    <>
      <section className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA]">Insights</div>
          <h1 className="ek-heading text-4xl md:text-5xl lg:text-6xl mt-4 text-white max-w-4xl leading-tight" data-testid="insights-headline">
            Sharp thinking on transformation, AI, delivery and value.
          </h1>
          <p className="text-zinc-200 font-light text-lg md:text-xl leading-relaxed mt-8 max-w-3xl">
            Short, practical perspectives from Ekselens — written for executives, transformation leaders and PMO directors looking for clear thinking, not noise.
          </p>
        </div>
      </section>

      <section className="bg-[#F2F2F3] ek-section">
        <div className="ek-container">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="ek-card animate-pulse h-64 bg-white" />
              ))}
            </div>
          ) : showPlaceholders ? (
            <div>
              <p className="ek-body text-lg mb-10 max-w-3xl">
                Articles are being prepared. Below are the upcoming pieces in our editorial pipeline.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {PLACEHOLDER_TITLES.map((t, i) => (
                  <div key={i} className="ek-card opacity-80" data-testid={`insight-placeholder-${i}`}>
                    <StarMotif size={18} color="#7737F6" />
                    <div className="text-[11px] font-semibold tracking-[0.18em] text-zinc-500 uppercase mt-5">Coming soon</div>
                    <h3 className="ek-heading text-lg text-black mt-2 leading-snug">{t}</h3>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ek-stagger">
              {insights.map((article, i) => (
                <Link
                  key={article.slug}
                  to={`/insights/${article.slug}`}
                  className="ek-card block group"
                  data-testid={`insight-card-${article.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <StarMotif size={18} color="#7737F6" />
                    <div className="text-[11px] font-semibold tracking-[0.18em] text-zinc-500 uppercase">
                      0{i + 1}
                    </div>
                  </div>
                  <div className="mt-5 text-[11px] font-semibold tracking-[0.18em] text-[#7737F6] uppercase">
                    {article.category}
                  </div>
                  <h3 className="ek-heading text-xl md:text-[22px] text-black mt-2 leading-snug">
                    {article.title}
                  </h3>
                  <p className="ek-body text-[14.5px] mt-3 line-clamp-3">{article.summary}</p>
                  <div className="mt-6 flex items-center justify-between text-[12.5px] text-zinc-500">
                    <span>{formatDate(article.published_at)}</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock size={12} /> {article.reading_minutes} min read
                    </span>
                  </div>
                  <div className="mt-5 inline-flex items-center gap-2 text-[#7737F6] text-sm font-medium">
                    Read article <ArrowRight size={14} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <NewsletterSignup />
      <CTABanner />
    </>
  );
};

export default Insights;
